import type {
  ExecutionArtifactRefRole,
  TaskArtifactView,
  TaskArtifactViewKind,
} from '@/lib/api/generated/model';

/**
 * How a thing the task created is written for a reader (spec 119 FR-003,
 * FR-007). `Record`s over the kind and the role, so a kind or role the
 * contract adds later fails to compile here rather than printing nothing.
 */

/** GitLab writes a merge request `group/repo!57`; GitHub a pull request `owner/repo#7`. */
const KIND_SIGIL: Record<TaskArtifactViewKind, string | null> = {
  merge_request: '!',
  pull_request: '#',
  branch: null,
};

export const ARTIFACT_KIND_WORD: Record<TaskArtifactViewKind, string | null> = {
  merge_request: 'merge request',
  pull_request: 'pull request',
  branch: null,
};

/** The short reference: `group/service!57`, `acme/tools#7`, or the branch name. */
export function artifactRef(artifact: TaskArtifactView): string {
  const sigil = KIND_SIGIL[artifact.kind];
  if (sigil === null || artifact.number === null) return artifact.source_branch;
  return `${artifact.repository}${sigil}${String(artifact.number)}`;
}

const CHANGE_VERB: Record<ExecutionArtifactRefRole, string> = {
  created: 'Opened',
  updated: 'Pushed to',
};

/**
 * What one run did to one artifact, as its group says it (FR-007):
 * "Opened group/service!57", "Pushed to group/service!57", or — a branch has
 * no request to open — "Pushed spike".
 */
export function runLineText(
  role: ExecutionArtifactRefRole,
  artifact: TaskArtifactView,
): string {
  if (artifact.kind === 'branch') return `Pushed ${artifact.source_branch}`;
  return `${CHANGE_VERB[role]} ${artifactRef(artifact)}`;
}

/** The first seven characters, as git itself abbreviates a commit. */
export function shortCommit(sha: string | null): string | null {
  return sha ? sha.slice(0, 7) : null;
}
